import {PbfReader} from 'pbf';
import {getArrayBuffer, ResourceType} from "../util/ajax";
import {readIconSetLazy, type Icon} from "../data/usvg/usvg_pb_decoder";
import browser from '../util/browser';

import type {Callback} from "../types/callback";
import type {RequestManager} from "../util/mapbox";
import type {StyleImage, StyleImages} from "./style_image";

function getContentArea(icon: Icon): [number, number, number, number] | undefined {
    if (!icon.metadata || !icon.metadata.content_area) {
        return undefined;
    }

    const dpr = browser.devicePixelRatio;
    const {left, top, width, height} = icon.metadata.content_area;

    const scaledLeft = left * dpr;
    const scaledTop = top * dpr;

    return [
        scaledLeft,
        scaledTop,
        scaledLeft + width * dpr,
        scaledTop + height * dpr
    ];
}

function getStretchArea(stretchArea: [number, number][] | undefined): [number, number][] | undefined {
    if (!stretchArea) {
        return undefined;
    }

    return stretchArea.map(([l, r]) => [l * browser.devicePixelRatio, r * browser.devicePixelRatio]);
}

export async function loadIconset(
    loadURL: string,
    requestManager: RequestManager,
    signal: AbortSignal,
    callback: Callback<StyleImages>
) {
    try {
        const request = await requestManager.transformRequest(requestManager.normalizeIconsetURL(loadURL), ResourceType.Iconset, signal);
        const {data} = await getArrayBuffer(request, signal);
        const result: StyleImages = {};

        const iconSet = readIconSetLazy(new PbfReader(data));

        for (const icon of iconSet.icons) {
            const styleImage: StyleImage = {
                version: 1,
                pixelRatio: browser.devicePixelRatio,
                content: getContentArea(icon),
                stretchX: icon.metadata ? getStretchArea(icon.metadata.stretch_x_areas) : undefined,
                stretchY: icon.metadata ? getStretchArea(icon.metadata.stretch_y_areas) : undefined,
                sdf: false,
                usvg: true,
                icon,
            };

            result[icon.name] = styleImage;
        }

        callback(null, result);
    } catch (err) {
        if (!signal.aborted) callback(err as Error);
    }
}
